/**
 * Fecha contable en formato AAAA-MM-DD.
 *
 * Es un tipo marcado porque esta fecha NO se deriva del reloj: se elige al abrir la
 * jornada y no se toca. La regla 7 de la spec dice que una noche del 31 de julio que
 * cierra a las 3 de la mañana del 1 de agosto pertenece a julio, para reportes,
 * cierre de mes y comparación de eventos. Todo reporte agrupa por esta fecha, jamás
 * por la fecha de creación del registro.
 */
export type FechaContable = string & { readonly __marca: 'FechaContable' };

const PATRON = /^(\d{4})-(\d{2})-(\d{2})$/;

export function fechaContable(iso: string): FechaContable {
  const m = PATRON.exec(iso);
  if (m === null) {
    throw new TypeError(`La fecha contable debe venir en formato AAAA-MM-DD, recibió ${JSON.stringify(iso)}`);
  }
  const [, aa, mm, dd] = m;
  const anio = Number(aa);
  const mes = Number(mm);
  const dia = Number(dd);
  const d = new Date(Date.UTC(anio, mes - 1, dia));
  if (d.getUTCFullYear() !== anio || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) {
    throw new RangeError(`La fecha ${iso} no existe`);
  }
  return iso as FechaContable;
}

/** Primer día del mes contable al que pertenece la fecha. */
export function mesDe(f: FechaContable): string {
  return `${f.slice(0, 7)}-01`;
}

/**
 * El primero del mes anterior, con aritmética de texto.
 *
 * Existe para la hoja de conteo físico de la fase 8, tarea 21, que ofrece contar el mes que corre o
 * el que acaba de terminar: el conteo de fin de mes se hace casi siempre los primeros días del mes
 * siguiente. Va sin `Date` por lo mismo que `mesSiguiente` en la pantalla de cierre: un `Date` a
 * medianoche se corre de mes según la zona de quien lo corre.
 */
export function mesAnterior(mes: string): string {
  const anio = Number(mes.slice(0, 4));
  const numero = Number(mes.slice(5, 7));
  return numero === 1
    ? `${String(anio - 1)}-12-01`
    : `${String(anio)}-${String(numero - 1).padStart(2, '0')}-01`;
}

/**
 * El primero del mes que cae `meses` después, con la misma aritmética de texto que `mesAnterior`.
 *
 * Existe para el término de un gasto fijo. `gasto_recurrente.vigente_hasta` es EXCLUSIVO, igual que
 * `parametro_vigencia`: un contrato de tres meses que arranca en septiembre se escribe
 * `vigente_hasta = 2026-12-01` y su última ocurrencia es la de noviembre. A nadie se le pide esa
 * fecha cruda, porque «hasta el 1 de diciembre» para decir «hasta noviembre» es exactamente el
 * error que la exclusividad provoca: se le pide el número de meses y esta función lo convierte.
 *
 * Va sin `Date` por lo mismo que `mesAnterior`: un `Date` a medianoche se corre de mes según la
 * zona de quien lo corre.
 */
export function mesMas(mes: string, meses: number): string {
  const anio = Number(mes.slice(0, 4));
  const numero = Number(mes.slice(5, 7));
  // Se cuenta en meses absolutos desde el año cero y se vuelve a partir al final. Sumar al número
  // de mes y corregir con un `if` solo funciona para saltos menores que doce.
  const total = anio * 12 + (numero - 1) + meses;
  return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

/** Colombia no tiene horario de verano, pero la zona se nombra igual: es la verdad, no un -5 escrito a mano. */
const ZONA = 'America/Bogota';

const DIA_EN_COLOMBIA = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * El día calendario colombiano en que ocurrió un instante.
 *
 * No se puede recortar los diez primeros caracteres del ISO, que es lo que parecía
 * alcanzar: la respuesta depende de cómo venga escrito el mismo instante. PostgREST
 * devuelve `timestamptz` en UTC, y Colombia es UTC-5, así que un cierre a las 11:30
 * de la noche del 31 llega como `2026-08-01T04:30:00+00:00`. Recortando, eso da el
 * 1 de agosto: un día distinto para el mismo momento.
 */
function diaEnColombia(instanteIso: string): string {
  const d = new Date(instanteIso);
  if (Number.isNaN(d.getTime())) {
    throw new TypeError(`El instante de cierre no es una fecha válida: ${JSON.stringify(instanteIso)}`);
  }
  return DIA_EN_COLOMBIA.format(d);
}

/**
 * Describe una noche que puede haber cruzado la medianoche.
 *
 * El cierre real solo sirve para informar al usuario que la noche cruzó. NUNCA se usa
 * para calcular la fecha contable, que ya venía decidida.
 */
export function nocheCruzaMedianoche(
  fecha: FechaContable,
  cierreIso: string,
): { fechaContable: FechaContable; mesContable: string; cruzoMedianoche: boolean } {
  const diaDelCierre = diaEnColombia(cierreIso);
  return {
    fechaContable: fecha,
    mesContable: mesDe(fecha),
    cruzoMedianoche: diaDelCierre !== fecha,
  };
}

/**
 * El día de hoy en Colombia, como fecha contable.
 *
 * Existe porque `new Date().toISOString().slice(0, 10)` es UTC, y en Cali eso adelanta el
 * día a partir de las siete de la noche. Para un gastrobar esa hora no es un caso raro: es
 * cuando abre. Un formulario que proponga esa fecha por omisión estaría fechando en el día
 * siguiente todo lo que se registre durante la operación de la noche, y el rechazo tampoco
 * llegaría, porque `app.assert_periodo_abierto` acepta el día siguiente sin protestar
 * mientras el mes esté abierto.
 *
 * Es una PROPUESTA para un campo que el usuario puede cambiar, nunca la fecha contable de
 * una jornada: esa se elige al abrirla y no se deriva del reloj.
 */
export function hoyEnColombia(ahora: Date = new Date()): FechaContable {
  return fechaContable(DIA_EN_COLOMBIA.format(ahora));
}
